// Dismissing a captured duplicate — the pure parts.
//
// ── Why this exists ───────────────────────────────────────────────────────
// Dismiss had no durable effect. It inserted a handled_actions row and PATCHed
// entries.action_note, but Needs Action reads entries WHERE action_type IS NOT
// NULL — a predicate neither write touches. The only thing removing the row
// from the list was React state, so every reload brought it back. Entry 1620
// (invoice 25061-DUP) was dismissed three times and never moved.
//
// Clearing action_type is not the fix — it IS the promote path. Every total
// excludes a row by `actionType !== "duplicate"` (App.js 357, 569, 864, 3057,
// 3117, 3126), so clearing it turns a dismissal into a counted sale, which is
// the exact mistake the split Dismiss / Promote buttons exist to prevent.
//
// action_type was carrying two meanings at once — "excluded from every total"
// and "still awaiting review" — and Dismiss has to end the second without
// touching the first. dismissed_at is that second meaning given its own column.
// action_type still means precisely what it meant before, and every total that
// reads it is untouched.
//
// These live outside App.js so the state machine can be tested without
// rendering the app; nothing here touches Supabase.

/** The note recorded when the operator dismisses without typing one. */
export const DISMISS_NOTE = 'Dismissed as duplicate'

/**
 * Is this entry still awaiting review?
 *
 * The JS mirror of the SQL predicate in fetchUnhandledActions:
 *
 *   action_type is not null and dismissed_at is null
 *
 * Both exist because the list is built twice — once by the server on load, and
 * again in React state as rows are handled. They must agree; that is what the
 * "survives a refetch" tests pin. Change one and change the other.
 */
export function isPendingAction(entry) {
  if (!entry) return false
  return entry.actionType != null && entry.dismissedAt == null
}

/** The pending set, in list order. */
export function filterPendingActions(entries) {
  return (entries || []).filter(isPendingAction)
}

/**
 * The write that makes a dismissal stick.
 *
 * actionType is deliberately absent — not omitted by oversight. entryToDb only
 * emits columns whose keys are present, so leaving it out is what guarantees
 * the duplicate stays excluded from every total.
 */
export function dismissPatch(note, nowIso) {
  const typed = typeof note === 'string' ? note.trim() : ''
  return {
    actionNote: typed || DISMISS_NOTE,
    dismissedAt: nowIso,
  }
}

/**
 * The write that puts a handled item back in front of the operator.
 *
 * dismissedAt: null is what returns a dismissed duplicate to pending. It is
 * explicitly null rather than absent for the same reason as above — entryToDb
 * keys off presence, so an omitted field would leave the stamp in place and
 * undo would appear to do nothing.
 *
 * statusComment is ABSENT here on purpose, and must stay absent. Do not add it
 * alongside actionNote: null on the reasoning that both are free text. They
 * clear for opposite reasons. actionNote described how the item was CLOSED and
 * the item is no longer closed, so it goes. statusComment describes the
 * situation the item is IN — "waiting for AGL feedback" — and undo is the
 * exact moment that context matters most: it puts the item back in front of an
 * operator who now has to decide what to do with it. Nulling it here would
 * delete the answer at the instant the question is asked.
 *
 * Pinned by 'Undo must not clear the comment' in statusComment.test.js.
 */
export function undismissPatch(handledAction) {
  const restoreType = (handledAction && handledAction.action_type) || '3rd_party'
  return {
    needs3rdParty: restoreType === '3rd_party',
    flaggedItems: (handledAction && handledAction.flagged_items) || [],
    actionType: restoreType,
    actionNote: null,
    dismissedAt: null,
  }
}

/**
 * An existing dismissal log for this entry, or null.
 *
 * The guard against re-logging. A duplicate reaches handled_actions by exactly
 * one route — dismissDuplicate — because the Needs Action row renders Dismiss /
 * Promote for action_type 'duplicate' and "Mark handled" for everything else,
 * and promote never logs. So a 'duplicate' row here means "already dismissed",
 * and a second dismissal should reuse it rather than stack another.
 *
 * Entry 1620 has three of these; the migration keeps the earliest.
 */
export function findDismissalLog(handledActions, entryId) {
  if (entryId == null) return null
  const found = (handledActions || []).filter(
    (ha) => ha && Number(ha.entry_id) === Number(entryId) && ha.action_type === 'duplicate'
  )
  if (!found.length) return null
  // Earliest wins, matching the migration's dedupe, so the client and the table
  // agree on which row is the dismissal of record.
  return found.reduce((a, b) => (rank(a) <= rank(b) ? a : b))
}

function rank(ha) {
  const t = Date.parse(ha.handled_at)
  return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER
}

/**
 * Did this error come from a unique index?
 *
 * The partial unique index on handled_actions (entry_id) where action_type =
 * 'duplicate' is the race-proof half of the guard — findDismissalLog only sees
 * what this tab has loaded, and two admins with the item open both see a row
 * that has never been dismissed. The loser of that race gets 23505 and should
 * adopt the winner's log row, not show an error for work that was done.
 *
 * Matches the string form too: PostgREST surfaces the code in the message on
 * some paths, which is how App.js's promote-collision handler already reads it.
 */
export function isUniqueViolation(err) {
  if (!err) return false
  if (err.code === '23505') return true
  const msg = String(err.message || err)
  return msg.includes('23505') || msg.toLowerCase().includes('duplicate key')
}
